interface ProfileData {
  name: string;
  tagline: string;
  location: string;
  phone: string;
  email: string;
  bio: string;
  profile_image: string;
  profileUrl: string;
  social_links: any[];
  custom_links: any[];
  collaborations: any[];
  updatedAt: string;
  [key: string]: any; // Allow additional properties
}

const DB_NAME = 'ProfileCache';
const DB_VERSION = 1;
const STORE_NAME = 'profiles';

export const openDatabase = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'profileUrl' });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
};

export const saveProfile = async (profileUrl: string, profileData: Partial<ProfileData>): Promise<void> => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const data = {
      ...profileData,
      profileUrl,
      updatedAt: new Date().toISOString()
    };
    
    await new Promise<void>((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving profile to cache:', error);
  }
};

export const getProfile = async (profileUrl: string): Promise<ProfileData | null> => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.get(profileUrl);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting profile from cache:', error);
    return null;
  }
};

export const clearProfileCache = async (profileUrl: string): Promise<void> => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    await new Promise<void>((resolve, reject) => {
      const request = store.delete(profileUrl);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing profile cache:', error);
  }
};

export const checkForUpdates = async (profileUrl: string, apiUrl: string): Promise<boolean> => {
  try {
    const cachedProfile = await getProfile(profileUrl);
    if (!cachedProfile) return true;
    
    // Fetch latest from API
    const response = await fetch(`${apiUrl}/profiles/${profileUrl}`);
    if (!response.ok) return false;
    
    const data = await response.json();
    if (!data.success) return false;
    
    const latestProfile = data.profile;
    
    // Compare actual data instead of timestamps
    // Check if social_links, custom_links, or collaborations have changed
    const cachedSocialLinks = JSON.stringify(cachedProfile.social_links || []);
    const latestSocialLinks = JSON.stringify(latestProfile.social_links || []);
    
    const cachedCustomLinks = JSON.stringify(cachedProfile.custom_links || []);
    const latestCustomLinks = JSON.stringify(latestProfile.custom_links || []);
    
    const cachedCollaborations = JSON.stringify(cachedProfile.collaborations || []);
    const latestCollaborations = JSON.stringify(latestProfile.collaborations || []);

    // UPI-related fields
    const cachedUpiId = cachedProfile.upi_id || null;
    const latestUpiId = latestProfile.upi_id || null;

    const cachedUpiQr = cachedProfile.upi_qr || null;
    const latestUpiQr = latestProfile.upi_qr || null;

    const cachedUpiPayments = JSON.stringify(cachedProfile.upi_payments || []);
    const latestUpiPayments = JSON.stringify(latestProfile.upi_payments || []);
    
    // Contact form fields - normalize null/undefined/empty string to null for comparison
    const normalizeString = (val: any): string | null => {
      if (val === null || val === undefined || val === '') return null;
      return String(val).trim() || null;
    };
    
    // Check if cached profile has these fields (might be missing if cached before feature was added)
    const cachedHasContactFields = 'contact_form_title' in cachedProfile || 'contact_form_button_text' in cachedProfile;
    const latestHasContactFields = 'contact_form_title' in latestProfile || 'contact_form_button_text' in latestProfile;
    
    // If cached profile doesn't have these fields but latest does (or vice versa), it's a change
    if (cachedHasContactFields !== latestHasContactFields) {
      console.log('[ProfileCache] Contact form fields presence changed');
      return true;
    }
    
    const cachedContactFormTitle = normalizeString(cachedProfile.contact_form_title);
    const latestContactFormTitle = normalizeString(latestProfile.contact_form_title);
    
    const cachedContactFormButtonText = normalizeString(cachedProfile.contact_form_button_text);
    const latestContactFormButtonText = normalizeString(latestProfile.contact_form_button_text);
    
    // Contact form enabled field
    const cachedContactFormEnabled = cachedProfile.contact_form_enabled !== false; // Default to true
    const latestContactFormEnabled = latestProfile.contact_form_enabled !== false; // Default to true
    
    // Contact form email field
    const cachedContactFormEmail = normalizeString(cachedProfile.contact_form_email);
    const latestContactFormEmail = normalizeString(latestProfile.contact_form_email);
    
    // Check if any data has changed
    const hasChanges = cachedSocialLinks !== latestSocialLinks ||
           cachedCustomLinks !== latestCustomLinks ||
           cachedCollaborations !== latestCollaborations ||
           cachedProfile.name !== latestProfile.name ||
           cachedProfile.tagline !== latestProfile.tagline ||
           cachedProfile.bio !== latestProfile.bio ||
           cachedProfile.profile_image !== latestProfile.profile_image ||
           cachedUpiId !== latestUpiId ||
           cachedUpiQr !== latestUpiQr ||
           cachedUpiPayments !== latestUpiPayments ||
           cachedContactFormTitle !== latestContactFormTitle ||
           cachedContactFormButtonText !== latestContactFormButtonText ||
           cachedContactFormEnabled !== latestContactFormEnabled ||
           cachedContactFormEmail !== latestContactFormEmail;
    
    // Debug logging (can be removed in production)
    if (hasChanges) {
      console.log('[ProfileCache] Changes detected:', {
        contactFormTitle: { cached: cachedContactFormTitle, latest: latestContactFormTitle },
        contactFormButtonText: { cached: cachedContactFormButtonText, latest: latestContactFormButtonText }
      });
    }
    
    return hasChanges;
  } catch (error) {
    console.error('Error checking for updates:', error);
    return false;
  }
};



